/**
 * Pure parser for the midterms map embed's iframe URL
 * (/external-widgets/midterms/{map}?…). Kept free of React and Next so the
 * contract partners embed against is unit tested directly.
 */

import type { MidtermsEmbedLinkMode } from 'app/(explore)/(no-right-sidebar)/category/[slug]/midterms/MidtermsEmbedContext';
import { MIDTERMS_TAB_PATHS } from 'app/(explore)/(no-right-sidebar)/category/[slug]/midterms/midtermsRoutes';

import { WIDGET_UTM_MEDIUM } from '../widgets/defaults';
import { booleanParam, embedPaletteFromParams, type ReadableParams } from './embedParams';

export const MIDTERMS_EMBED_MAPS = ['senate', 'house', 'governor', 'balance-of-power'] as const;
export type MidtermsEmbedMap = (typeof MIDTERMS_EMBED_MAPS)[number];

export interface MidtermsEmbedOptions {
    map: MidtermsEmbedMap;
    /** `new-tab` (default) opens kalshi.com pages in a new tab; `none` is a purely informational embed. */
    links: MidtermsEmbedLinkMode;
    /** Show the forecast card(s) above the map / chart. Default on. */
    showForecast: boolean;
    /** Show the timeline slider under the map. Default off; ignored for balance-of-power. */
    showTimeline: boolean;
    theme: 'light' | 'dark';
    /** Optional partner identifier, for attribution in analytics and on outbound links. */
    partner: string | null;
    utmSource: string | null;
    utmCampaign: string | null;
    referral: string | null;
}

/** Hub path (on kalshi.com) each embed's brand link points at. */
export const MIDTERMS_EMBED_HUB_PATHS: Record<MidtermsEmbedMap, string> = {
    senate: MIDTERMS_TAB_PATHS.senate,
    house: MIDTERMS_TAB_PATHS.house,
    governor: MIDTERMS_TAB_PATHS.governor,
    'balance-of-power': MIDTERMS_TAB_PATHS.balanceOfPower,
};

export const MIDTERMS_EMBED_TITLES: Record<MidtermsEmbedMap, string> = {
    senate: '2026 Senate Forecast',
    house: '2026 House Forecast',
    governor: '2026 Governor Forecast',
    'balance-of-power': 'American Power Index',
};

/** Longest attribution value we'll carry; anything longer is dropped, not truncated. */
const MAX_ATTRIBUTION_LENGTH = 100;
/**
 * Partner ids are identifiers, not prose: letters, digits, `-`, `_`, `.`.
 * A regex character-class body, shared with the gallery's Partner ID field so
 * it can't produce embed code this parser would drop.
 */
export const MIDTERMS_PARTNER_ID_CHARACTERS = 'A-Za-z0-9._-';
const PARTNER_PATTERN = new RegExp(`^[${MIDTERMS_PARTNER_ID_CHARACTERS}]+$`);

export function isMidtermsEmbedMap(value: string): value is MidtermsEmbedMap {
    return (MIDTERMS_EMBED_MAPS as readonly string[]).includes(value);
}

function attributionParam(params: ReadableParams, name: string): string | null {
    const value = params.get(name)?.trim();
    if (!value || value.length > MAX_ATTRIBUTION_LENGTH) return null;
    return value;
}

export function parseMidtermsEmbedOptions(map: MidtermsEmbedMap, params: ReadableParams): MidtermsEmbedOptions {
    const partner = attributionParam(params, 'partner');
    return {
        map,
        links: params.get('links') === 'none' ? 'none' : 'new-tab',
        showForecast: booleanParam(params, 'forecast', true),
        showTimeline: map !== 'balance-of-power' && booleanParam(params, 'timeline', false),
        theme: embedPaletteFromParams(params),
        partner: partner !== null && PARTNER_PATTERN.test(partner) ? partner : null,
        utmSource: attributionParam(params, 'utm_source'),
        utmCampaign: attributionParam(params, 'utm_campaign'),
        referral: attributionParam(params, 'referral'),
    };
}

/** Attribution params appended to every outbound kalshi.com link. */
export function midtermsEmbedOutboundParams(options: MidtermsEmbedOptions): Record<string, string> {
    const params: Record<string, string> = { utm_medium: WIDGET_UTM_MEDIUM };
    if (options.utmSource) params.utm_source = options.utmSource;
    if (options.utmCampaign) params.utm_campaign = options.utmCampaign;
    if (options.referral) params.referral = options.referral;
    if (options.partner) params.partner = options.partner;
    return params;
}
